import { Injectable } from '@angular/core';
import { LanguageProvider } from './language.provider';
import { LocalStorageService } from './storage/local-storage.service';
import { StorageService } from './storage/storage.service';
import { GeneralExerciseComponent } from '../General/g-exercise.component';
import { GeneralTeacherComponent } from '../General/gt.component';
import { Router, RouterEvent, NavigationStart } from '@angular/router';
import { MessageService } from '../shared/message.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ElectronCloseModalComponent } from './electron-close-modal/electron-close-modal.component';

// import { saveAs } from 'file-saver/FileSaver';

@Injectable()
export class AppService {

  configuration: any;
  language: any;
  state: any = {
    tipps: [],
    topics: []
  };
  storageService: StorageService = null;

  loadedConfig = false;
  loadedState = false;

  showTipps = false;

  currentExercise: GeneralExerciseComponent = undefined;
  currentTeacherExercise: GeneralTeacherComponent = undefined;

  changeLanguageCallbacks: ((language: string) => void)[] = [];
  languageAbbreviation = 'de';


  closeWindow = false;

  constructor(private languageProvider: LanguageProvider, private localStorageService: LocalStorageService, private router: Router,
              private messageService: MessageService, protected modalService: NgbModal) {
    this.language = languageProvider.language;
    this.storageService = localStorageService;

    const userAgent = window.navigator.userAgent;
    console.log(userAgent);

    // load the configuration and state from sessionStorage if possible
    if (sessionStorage.getItem('configuration')) {
      this.configuration = JSON.parse(sessionStorage.getItem('configuration'));
      this.loadedConfig = true;
    }
    if (sessionStorage.getItem('state')) {
      this.state = JSON.parse(sessionStorage.getItem('state'));
      this.loadedState = true;
    }

    // ask for confirmation when closing the window
    if (userAgent.indexOf(' Electron/') < 0 && userAgent.indexOf(' electron/') < 0) {
      console.log('Browser');
      window.onbeforeunload = function (e) {
        e.returnValue = 'Do you want to save?';
      };
    } else {
      console.log('electron');

      window.addEventListener('beforeunload', evt => {
        if (this.closeWindow) { return; }

        evt.returnValue = false;

        setTimeout(() => {
          /*let result = confirm("Do you want to close?");

          if (result) {
            this.closeWindow = true;
            window.close();
          }*/
          this.open();
        });
      });
    }



    // ensures to always start from top of a page
    // not optimal, but best we can do with little effort
    router.events.subscribe(() => {
      window.scrollTo(0, 0);
      this.messageService.reset();
    });

    // ensures that the next-button works correctly
    this.router.routeReuseStrategy.shouldReuseRoute = function () {
      return false;
    };
  }

  /** asks for confirmation to open if necessary */
  open() {
    const self = this;
    const modal = this.modalService.open(ElectronCloseModalComponent);
    modal.componentInstance.appService = this;

    modal.result.then((result) => {
      self.closeWindow = true;
      window.close();
    }, (reason) => {

    });
  }

  /** get text in right language
   * @param {string} tag - text to load
   */
  get(tag: string): string {
    return this.language[tag];
  }

  /** return the title of the book */
  getTitle(): String {
    return this.get('booktitle');
  }

  /** return the author of the configuration */
  getAuthor(): string {
    return this.configuration['author'];
  }

  /** return the date of the configuration */
  getDate(): Date {
    const datestring = this.configuration['date'];
    return new Date(datestring);
  }

  /** return the key of the configuration */
  getKey(): string {
    return this.configuration['key'];
  }

  /** return the version of the configuration */
  getVersion(): Number {
    return this.configuration['version'];
  }

  /** return an array of all topics in hte configuration */
  getAllTopicsTitle(): string[] {
    const allTopics: any[] = this.configuration['topics'];
    const result: string[] = [];
    for (const topic of allTopics) {
      result.push(topic['title']);
    }
    return result;
  }

  /** returns all topics with id, title, type and image */
  getAllTopics(): any[] {
    if (this.configuration === undefined) {
      return [];
    }
    const allTopics: any[] = this.configuration['topics'];
    const result: any[] = [];
    let temp: any = {};
    for (const topic of allTopics) {
      temp.id = topic['id'];
      temp.image = topic['image'];
      temp.title = topic['title'];
      temp.type = topic['type'];
      result.push(temp);
      temp = {};
    }
    return result;
  }

  /** returns the configuration for the requested topic */
  getTopicConfiguration(type: string): any {
    console.log('GET TOPIC CONFIGURATION');
    const allTopics: any[] = this.configuration['topics'];
    for (const topic of allTopics) {
      if (topic['type'] === type) {
        return topic;
      }
    }
    allTopics.push(
      {
        id: allTopics.length,
        type: type,
        title: this.get(type),
        image: './assets/images/' + type + '.png',
        typespecifics: [],
        exercises: []
      }
    );
    return allTopics[allTopics.length - 1];
  }

  /** returns the state for the requested topic */
  getTopicState(type: string): any {
    let allTopics: any[];
    if (this.state !== undefined && this.state != null) {

      allTopics = this.state['topics'];
      for (const topic of allTopics) {
        if (topic['type'] === type) {
          return topic;
        }
      }
    }
    // console.log(this.allTopics);
    allTopics.push({
      type: type,
      exercises: []
    });
    return allTopics[allTopics.length - 1];
  }


  /** return whether the teachers place is visible or not */
  isTeachersPlaceVisible(): boolean {
    if (this.configuration !== undefined && this.configuration != null) {
      return this.configuration['teachersplace'];
    }
    return false;
  }

  /** updates the configuration of the given type
   * @param {string} type - holds the typestring of the topic to update
   * @param {any} definition - holds the configuration to update
   */
  updateConfiguration(type: string, definition: any) {
    const id = this.getPositionOfTopic(type, this.configuration);
    if (id >= 0) {
      if (definition.exercises.length === 0) {
        this.configuration.topics.splice(id, 1);
      } else {
        this.configuration.topics[id] = definition;
      }
    }
    this.storeConfigLocally();
  }

  /** sets state for given type
   * @param {string} type - type to update
   * @param {any} state - new state to set
   */
  setState(type: string, state: any) {
    const id = this.getPositionOfTopic(type, this.state);
    if (id > 0) {
      this.state.topics[id] = state;
    }
    this.storeStateLocally();
  }

  /** returns position of this topic in array
   * @param {string} type - type to find
   * @param {any} element - json to search in, state or configuration
   */
  getPositionOfTopic(type: string, element: any) {
    const topics = element['topics'];
    let index = 0;
    for (const topic of topics) {
      if (topic.type === type) {
        return index;
      }
      index++;
    }
    index = -1;
    return index;
  }

  /** gets called by the configuration loader */
  onLoadedConfig() {
    this.loadedConfig = true;
    this.storeConfigLocally();
    if (!this.loadedState) {
      this.generateState();
      this.storeStateLocally();
    }
    this.messageService.addAlertWithTimeout(this.get('loadedConfig'), 'success', 10000);
  }

  /** gets called by the configuration loader */
  onLoadedState(state: any) {
    for (const topic of state.topics) {
      const oldTopic = this.getTopicState(topic.type);
      if (!oldTopic.exercises) {
        oldTopic.exercises.push(...topic.exercises);
        continue;
      }
      const oldKeys: string[] = oldTopic.exercises.map(v => v.key);
      for (const exercise of topic.exercises) {
        if (!oldTopic.exercises.map(v => v.key).includes(exercise.key)) {
          oldTopic.exercises.push(exercise);
        }
      }
    }
    this.storeStateLocally();
    this.loadedState = true;
    this.messageService.addAlertWithTimeout(this.get('loadedState'), 'success', 10000);
  }

  /** load the configuration
   * @param {any} inputvalue - holds the information needed to load the configuration
   */
  loadConfig(inputValue: any): void {
    this.storageService.loadConfig(inputValue, this);
  }

  /** load the state
   * @param {any} inputvalue - holds the information needed to load the configuration
   */
  loadState(inputValue: any): void {
    this.storageService.loadState(inputValue, this);
  }
  /** store the configuration */
  storeConfigTeacher(): boolean {
    let result = true;
    this.cleanUpConfiguration();
    result = result && this.storageService.storeConfig(this.configuration, 'teacher_', this);
    return result;
  }

  /** store the configuration for students */
  storeConfigStudent(): boolean {
    let result = true;
    this.cleanUpConfiguration();
    this.configuration.teachersplace = false;
    result = result && this.storageService.storeConfig(this.configuration, '', this);
    this.configuration.teachersplace = true;
    return result;
  }

  /** removes chapter not containing any exercise or exercise set */
  cleanUpConfiguration() {
    const allTopics = [...this.configuration['topics']];
    for (const topic of allTopics) {
      if (topic.exercises.length === 0) {
        const index = this.configuration['topics'].map((v, i) => [v, i]).filter(v => v[0].id === topic.id).map(v => v[1])[0];
        this.configuration['topics'].splice(index, 1);
      }
    }
  }


  /** store the state */
  storeState(): boolean {
    return this.storageService.storeState(this.state, this);
  }

  /**store the configuration locally */
  storeConfigLocally(): void {
    sessionStorage.setItem('configuration', JSON.stringify(this.configuration));
  }

  /** store the state locally */
  storeStateLocally(): void {
    sessionStorage.setItem('state', JSON.stringify(this.state));
  }

  /** generates an empty state document based on the given configuration */
  generateState() {
    const allTopics = this.getAllTopics();
    this.state.topics = [];
    for (const topic of allTopics) {
      this.state.topics.push({ 'type': topic.type, 'exercises': [] });
    }
  }

  /** registers a new callback function for language change */
  registerLanguageChangeCallback(callback: ((language: string) => void)) {
    this.changeLanguageCallbacks.push(callback);
  }

  /** asks the language provider to change the language */
  changeLanguage(language: string) {
    this.languageAbbreviation = language;
    this.languageProvider.loadLanguage(language).then(language => this.language = this.languageProvider.getLanguage());
    for (const callback of this.changeLanguageCallbacks) {
      callback(language);
    }
  }

  /** decides whether or not the tip of the given type is shown */
  showTip(name: string, isVideo: boolean = false): boolean {
    if (!this.showTipps && !isVideo) {
      return false;
    }
    if (this.state.tipps.includes(name)) {
      return false;
    }
    return true;
  }

  /** adds the given type to the state */
  closeTip(name: string) {
    if (!this.state.tipps.includes(name)) {
      this.state.tipps.push(name);
      this.storeStateLocally();
    }
  }

}
